import React, { useState } from 'react';
import { Link, Outlet, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasStoredPermission } from '@/features/auth/utils/jwtPermissions';
import AdminLanguageSwitcher from '../components/AdminLanguageSwitcher';
import './AdminLayout.css';
import logo from '../../../assets/images/phuong-dong-logo-horizontal-blue-on-white.png';

const AdminLayout: React.FC = () => {
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const canReadUsers = hasStoredPermission('USER_READ_ALL');
  const canReadSaleHistory = hasStoredPermission('SALE_READ_ALL')
    || hasStoredPermission('SALE_READ_OWN');

  return (
    <div className="bg-background text-on-background min-h-screen flex font-body-md text-body-md overflow-hidden">
      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* SideNavBar - Desktop: fixed, Mobile: overlay */}
      <aside className={`
        flex flex-col h-screen fixed left-0 top-0 bg-white border-r border-outline-variant py-md px-sm overflow-y-auto z-50 transition-all duration-300
        ${isMobileSidebarOpen ? 'translate-x-0 w-60' : '-translate-x-full w-60'}
        md:translate-x-0 ${isSidebarOpen ? 'md:w-60' : 'md:w-20'}
      `}>
        <div className={`mb-lg px-3 flex items-center gap-3 justify-center ${!isSidebarOpen && 'md:pt-2'}`}>
          <Link to="/" aria-label={t('admin.layout.home')}>
            {isSidebarOpen ? (
              <img alt="Phương Đông Logo" className="h-16 w-auto object-contain" src={logo} />
            ) : (
              <span className="font-bold text-primary text-xl hidden md:block">PĐ</span>
            )}
          </Link>
          {/* Mobile close button */}
          <button
            className="md:hidden ml-auto p-1 text-secondary hover:text-on-surface"
            onClick={() => setIsMobileSidebarOpen(false)}
            aria-label={t('admin.layout.close_navigation')}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <nav className="flex flex-col gap-1 flex-grow font-label-md text-label-md">
          <NavLink 
            to="/admin" 
            end 
            className={({ isActive }) => `flex items-center gap-3 px-3 ${!isSidebarOpen ? 'md:justify-center md:px-0 md:gap-0' : ''} py-2 rounded-lg transition-all duration-150 ${isActive ? 'text-primary font-bold bg-secondary-container scale-[0.98]' : 'text-secondary hover:bg-surface-container-high'}`}
            title={t('admin.layout.dashboard_description')}
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            {({ isActive }) => (
              <>
                <span className="material-symbols-outlined" style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}>dashboard</span>
                <span className={!isSidebarOpen ? 'md:hidden' : ''}>{t('admin.layout.dashboard')}</span>
              </>
            )}
          </NavLink>
          <NavLink 
            to="/admin/products" 
            className={({ isActive }) => `flex items-center gap-3 px-3 ${!isSidebarOpen ? 'md:justify-center md:px-0 md:gap-0' : ''} py-2 rounded-lg transition-all duration-150 ${isActive ? 'text-primary font-bold bg-secondary-container scale-[0.98]' : 'text-secondary hover:bg-surface-container-high'}`}
            title={t('admin.products.nav_description')}
            onClick={() => setIsMobileSidebarOpen(false)}
          >
             {({ isActive }) => (
              <>
                <span className="material-symbols-outlined" style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}>inventory_2</span>
                <span className={!isSidebarOpen ? 'md:hidden' : ''}>{t('admin.products.nav_title')}</span>
              </>
             )}
          </NavLink>
          <NavLink
            to="/admin/feedbacks"
            className={({ isActive }) => `flex items-center gap-3 px-3 ${!isSidebarOpen ? 'md:justify-center md:px-0 md:gap-0' : ''} py-2 rounded-lg transition-all duration-150 ${isActive ? 'text-primary font-bold bg-secondary-container scale-[0.98]' : 'text-secondary hover:bg-surface-container-high'}`}
            title={t('admin.feedbacks.nav_description')}
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            {({ isActive }) => (
              <>
                <span className="material-symbols-outlined" style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}>reviews</span>
                <span className={!isSidebarOpen ? 'md:hidden' : ''}>{t('admin.feedbacks.nav_title')}</span>
              </>
            )}
          </NavLink>
          <NavLink
            to="/admin/articles"
            className={({ isActive }) => `flex items-center gap-3 px-3 ${!isSidebarOpen ? 'md:justify-center md:px-0 md:gap-0' : ''} py-2 rounded-lg transition-all duration-150 ${isActive ? 'text-primary font-bold bg-secondary-container scale-[0.98]' : 'text-secondary hover:bg-surface-container-high'}`}
            title={t('admin.articles.nav_description')}
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            {({ isActive }) => (
              <>
                <span className="material-symbols-outlined" style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}>article</span>
                <span className={!isSidebarOpen ? 'md:hidden' : ''}>{t('admin.articles.nav_title')}</span>
              </>
            )}
          </NavLink>
          <NavLink
            to="/admin/sales"
            end
            className={({ isActive }) => `flex items-center gap-3 px-3 ${!isSidebarOpen ? 'md:justify-center md:px-0 md:gap-0' : ''} py-2 rounded-lg transition-all duration-150 ${isActive ? 'text-primary font-bold bg-secondary-container scale-[0.98]' : 'text-secondary hover:bg-surface-container-high'}`}
            title={t('admin.layout.sales_description')}
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            {({ isActive }) => (
              <>
                <span className="material-symbols-outlined" style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}>point_of_sale</span>
                <span className={!isSidebarOpen ? 'md:hidden' : ''}>{t('admin.layout.sales')}</span>
              </>
            )}
          </NavLink>
          {canReadSaleHistory && (
            <NavLink
              to="/admin/sales/history"
              className={({ isActive }) => `flex items-center gap-3 px-3 ${!isSidebarOpen ? 'md:justify-center md:px-0 md:gap-0' : ''} py-2 rounded-lg transition-all duration-150 ${isActive ? 'text-primary font-bold bg-secondary-container scale-[0.98]' : 'text-secondary hover:bg-surface-container-high'}`}
              title={t('admin.sales_history.nav_description')}
              onClick={() => setIsMobileSidebarOpen(false)}
            >
              {({ isActive }) => (
                <>
                  <span className="material-symbols-outlined" style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}>history</span>
                  <span className={!isSidebarOpen ? 'md:hidden' : ''}>{t('admin.sales_history.nav_title')}</span>
                </>
              )}
            </NavLink>
          )}
          {canReadUsers && (
            <NavLink
              to="/admin/users"
              className={({ isActive }) => `flex items-center gap-3 px-3 ${!isSidebarOpen ? 'md:justify-center md:px-0 md:gap-0' : ''} py-2 rounded-lg transition-all duration-150 ${isActive ? 'text-primary font-bold bg-secondary-container scale-[0.98]' : 'text-secondary hover:bg-surface-container-high'}`}
              title={t('admin.users.nav_description')}
              onClick={() => setIsMobileSidebarOpen(false)}
            >
              {({ isActive }) => (
                <>
                  <span className="material-symbols-outlined" style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}>manage_accounts</span>
                  <span className={!isSidebarOpen ? 'md:hidden' : ''}>{t('admin.users.nav_title')}</span>
                </>
              )}
            </NavLink>
          )}
          <NavLink
            to="/admin/settings"
            className={({ isActive }) => `flex items-center gap-3 px-3 ${!isSidebarOpen ? 'md:justify-center md:px-0 md:gap-0' : ''} py-2 rounded-lg transition-all duration-150 ${isActive ? 'text-primary font-bold bg-secondary-container scale-[0.98]' : 'text-secondary hover:bg-surface-container-high'}`}
            title={t('admin.settings.nav_description')}
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            {({ isActive }) => (
              <>
                <span className="material-symbols-outlined" style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}>settings</span>
                <span className={!isSidebarOpen ? 'md:hidden' : ''}>{t('admin.settings.nav_title')}</span>
              </>
            )}
          </NavLink>
        </nav>
      </aside>

      {/* Main Content Wrapper */}
      <div className={`flex-1 flex flex-col h-screen overflow-hidden transition-all duration-300 ${isSidebarOpen ? 'md:ml-60' : 'md:ml-20'}`}>
        {/* TopNavBar */}
        <header className="bg-white border-b border-outline-variant h-14 sm:h-16 flex justify-between items-center w-full px-3 sm:px-lg sticky top-0 z-30">
          <div className="flex items-center flex-shrink-0">
            <button 
              onClick={() => {
                // On mobile, toggle mobile overlay; on desktop, toggle collapsed sidebar
                if (window.innerWidth < 768) {
                  setIsMobileSidebarOpen(!isMobileSidebarOpen);
                } else {
                  setIsSidebarOpen(!isSidebarOpen);
                }
              }}
              className="p-2 text-secondary hover:bg-surface-container-low rounded-full transition-colors flex-shrink-0 flex items-center justify-center"
              aria-label={t('admin.layout.toggle_navigation')}
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
          </div>
          <div className="flex items-center gap-1 sm:gap-md flex-shrink-0">
            <AdminLanguageSwitcher />
            <button className="hidden sm:flex bg-primary text-on-primary font-label-md text-label-md px-4 py-2 rounded-DEFAULT hover:bg-primary-fixed-variant transition-colors items-center gap-2">
              <span className="material-symbols-outlined text-sm">add</span>
              {t('admin.layout.quick_create')}
            </button>
            <div className="flex items-center gap-1 sm:gap-2 sm:border-l border-outline-variant sm:pl-4">
              <button className="p-2 text-secondary hover:bg-surface-container-low rounded-full transition-colors relative" aria-label={t('admin.layout.notifications')}>
                <span className="material-symbols-outlined">notifications</span>
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-error rounded-full"></span>
              </button>
              <button className="hidden sm:block p-2 text-secondary hover:bg-surface-container-low rounded-full transition-colors" aria-label={t('admin.layout.store')}>
                <span className="material-symbols-outlined">store</span>
              </button>
              <button className="p-2 text-secondary hover:bg-surface-container-low rounded-full transition-colors" aria-label={t('admin.layout.account')}>
                <span className="material-symbols-outlined">account_circle</span>
              </button>
            </div>
          </div>
        </header>

        {/* Canvas */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-lg bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
